import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { CalendarBooking, Listing } from '../types';
import { useCalendarBookings } from './api/useCalendarBookings';
import { useListings } from './api/useListings';
import { getMonthStart, getMonthEnd, getNextMonth, getPreviousMonth } from '../utils/calendar-utils';
import { useDebounce } from './useDebounce';

interface CalendarState {
  currentMonth: Date;
  selectedDate: Date | null;
  selectedListings: string[];
  showDaySummary: boolean;
  includeExternal: boolean;
}

interface CalendarStateActions {
  setCurrentMonth: (date: Date) => void;
  setSelectedDate: (date: Date | null) => void;
  setSelectedListings: (listingIds: string[]) => void;
  setShowDaySummary: (show: boolean) => void;
  setIncludeExternal: (include: boolean) => void;
  handleDateSelect: (date: Date) => void;
  handleCloseDaySummary: () => void;
  handleMonthChange: (date: Date) => void;
  handleListingFilterChange: (listingIds: string[]) => void;
  navigateToNextMonth: () => void;
  navigateToPreviousMonth: () => void;
  resetToToday: () => void;
}

interface CalendarData {
  listings: Listing[];
  allBookings: CalendarBooking[];
  directBookings: CalendarBooking[];
  externalBookings: any[];
  syncStatus: Record<string, any>;
  conflicts: Array<{
    directBooking: CalendarBooking;
    externalBooking: CalendarBooking;
    conflictType: 'overlap' | 'exact_match';
  }>;
  bookingsForSelectedDate: CalendarBooking[];
}

interface CalendarLoadingState {
  isLoadingListings: boolean;
  isLoadingBookings: boolean;
  isLoadingAny: boolean;
  listingsError: Error | null;
  bookingsError: Error | null;
  hasError: boolean;
}

interface UseCalendarStateReturn {
  state: CalendarState;
  actions: CalendarStateActions;
  data: CalendarData;
  loading: CalendarLoadingState;
  // Advanced functionality
  prefetchNextMonth: () => Promise<void>;
  prefetchPreviousMonth: () => Promise<void>;
  invalidateBookings: () => Promise<void>;
  optimisticUpdateBooking: (bookingId: string, updates: Partial<CalendarBooking>) => void;
}

interface UseCalendarStateOptions {
  debounceMs?: number;
  enableUrlSync?: boolean;
  enablePrefetching?: boolean;
  autoSelectAllListings?: boolean;
}

export const useCalendarState = (options: UseCalendarStateOptions = {}): UseCalendarStateReturn => {
  const {
    debounceMs = 300,
    enableUrlSync = true,
    enablePrefetching = true,
    autoSelectAllListings = true,
  } = options;

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Core state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedListings, setSelectedListings] = useState<string[]>([]);
  const [showDaySummary, setShowDaySummary] = useState(false);
  const [includeExternal, setIncludeExternal] = useState(true);

  // Fetch listings data
  const { 
    data: listings = [], 
    isLoading: isLoadingListings, 
    error: listingsError 
  } = useListings({
    status: 'published' // Only show published listings
  });

  // Auto-select all listings when they load
  useEffect(() => {
    if (autoSelectAllListings && listings.length > 0 && selectedListings.length === 0) {
      setSelectedListings(listings.map(listing => listing.listingId));
    }
  }, [listings, selectedListings.length, autoSelectAllListings]);

  // Calculate date range for current month
  const startDate = useMemo(() => getMonthStart(currentMonth).toISOString().split('T')[0], [currentMonth]);
  const endDate = useMemo(() => getMonthEnd(currentMonth).toISOString().split('T')[0], [currentMonth]);

  // Fetch calendar bookings data with debouncing
  const calendarBookingsResult = useCalendarBookings({
    startDate,
    endDate,
    listingIds: selectedListings.length > 0 ? selectedListings : undefined,
    includeExternal,
    debounceMs,
  });

  const {
    data: calendarData,
    isLoading: isLoadingBookings,
    error: bookingsError,
    invalidateBookings,
    prefetchNextMonth,
    optimisticUpdateBooking,
  } = calendarBookingsResult;

  // Handle URL parameters and context restoration
  useEffect(() => {
    if (!enableUrlSync) return;

    const restoreContext = location.state?.restoreContext;
    const urlDate = searchParams.get('date');
    const urlMonth = searchParams.get('month');
    const urlListings = searchParams.get('listings');

    if (restoreContext && location.state) {
      // Restore from navigation state (coming back from booking details)
      if (location.state.selectedDate) {
        const date = new Date(location.state.selectedDate);
        setSelectedDate(date);
        setShowDaySummary(true);
      }
      if (location.state.currentMonth) {
        setCurrentMonth(new Date(location.state.currentMonth));
      }
      if (location.state.selectedListings) {
        setSelectedListings(location.state.selectedListings);
      }
    } else {
      // Handle URL parameters for deep linking
      if (urlDate) {
        try {
          const date = new Date(urlDate);
          if (!isNaN(date.getTime())) {
            setSelectedDate(date);
            setShowDaySummary(true);
          }
        } catch (e) {
          console.warn('Invalid date parameter:', urlDate);
        }
      }
      
      if (urlMonth) {
        try {
          const month = new Date(urlMonth);
          if (!isNaN(month.getTime())) {
            setCurrentMonth(month);
          }
        } catch (e) {
          console.warn('Invalid month parameter:', urlMonth);
        }
      }
      
      if (urlListings) {
        try {
          const listingIds = urlListings.split(',').filter(id => id.trim());
          if (listingIds.length > 0) {
            setSelectedListings(listingIds);
          }
        } catch (e) {
          console.warn('Invalid listings parameter:', urlListings);
        }
      }
    }
  }, [location.state, searchParams, enableUrlSync]);

  // Update URL parameters when state changes (for deep linking)
  useEffect(() => {
    if (!enableUrlSync) return;

    const params = new URLSearchParams();
    
    if (selectedDate) {
      params.set('date', selectedDate.toISOString().split('T')[0]);
    }
    
    if (currentMonth) {
      params.set('month', currentMonth.toISOString().split('T')[0]);
    }
    
    if (selectedListings.length > 0 && listings.length > 0) {
      // Only set listings param if not all listings are selected
      const allListingIds = listings.map(l => l.listingId);
      if (selectedListings.length !== allListingIds.length || 
          !selectedListings.every(id => allListingIds.includes(id))) {
        params.set('listings', selectedListings.join(','));
      }
    }
    
    // Update URL without triggering navigation
    const newSearch = params.toString();
    if (newSearch !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [selectedDate, currentMonth, selectedListings, listings, setSearchParams, searchParams, enableUrlSync]);

  // Prefetch adjacent months for better performance
  useEffect(() => {
    if (!enablePrefetching || isLoadingBookings) return;

    const prefetchAdjacentMonths = async () => {
      const nextMonth = getNextMonth(currentMonth);
      const nextMonthStart = getMonthStart(nextMonth).toISOString().split('T')[0];
      const nextMonthEnd = getMonthEnd(nextMonth).toISOString().split('T')[0];

      try {
        await prefetchNextMonth(nextMonthStart, nextMonthEnd);
      } catch (error) {
        // Silently fail prefetching
        console.debug('Failed to prefetch next month:', error);
      }
    };

    const timeoutId = setTimeout(prefetchAdjacentMonths, 1000);
    return () => clearTimeout(timeoutId);
  }, [currentMonth, prefetchNextMonth, isLoadingBookings, enablePrefetching]);

  // Action handlers
  const handleDateSelect = useCallback((date: Date) => {
    setSelectedDate(date);
    setShowDaySummary(true);
  }, []);

  const handleCloseDaySummary = useCallback(() => {
    setShowDaySummary(false);
  }, []);

  const handleMonthChange = useCallback((date: Date) => {
    setCurrentMonth(date);
  }, []);

  const handleListingFilterChange = useCallback((listingIds: string[]) => {
    setSelectedListings(listingIds);
  }, []);

  const navigateToNextMonth = useCallback(() => {
    setCurrentMonth(prev => getNextMonth(prev));
  }, []);

  const navigateToPreviousMonth = useCallback(() => {
    setCurrentMonth(prev => getPreviousMonth(prev));
  }, []);

  const resetToToday = useCallback(() => {
    const today = new Date();
    setCurrentMonth(today);
    setSelectedDate(today);
    setShowDaySummary(true);
  }, []);

  const prefetchPreviousMonth = useCallback(async () => {
    const prevMonth = getPreviousMonth(currentMonth);
    const prevMonthStart = getMonthStart(prevMonth).toISOString().split('T')[0];
    const prevMonthEnd = getMonthEnd(prevMonth).toISOString().split('T')[0];
    
    await prefetchNextMonth(prevMonthStart, prevMonthEnd);
  }, [currentMonth, prefetchNextMonth]);

  // Get bookings for the selected date
  const bookingsForSelectedDate = useMemo((): CalendarBooking[] => {
    if (!selectedDate || !calendarData?.allBookings) return [];
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    return calendarData.allBookings.filter((booking: CalendarBooking) => {
      const startDate = new Date(booking.startDate).toISOString().split('T')[0];
      const endDate = new Date(booking.endDate).toISOString().split('T')[0];
      return dateStr >= startDate && dateStr < endDate; // Exclusive end date
    });
  }, [selectedDate, calendarData?.allBookings]);

  // Prepare return object
  const state: CalendarState = {
    currentMonth,
    selectedDate,
    selectedListings,
    showDaySummary,
    includeExternal,
  };

  const actions: CalendarStateActions = {
    setCurrentMonth,
    setSelectedDate,
    setSelectedListings,
    setShowDaySummary,
    setIncludeExternal,
    handleDateSelect,
    handleCloseDaySummary,
    handleMonthChange,
    handleListingFilterChange,
    navigateToNextMonth,
    navigateToPreviousMonth,
    resetToToday,
  };

  const data: CalendarData = {
    listings,
    allBookings: calendarData?.allBookings || [],
    directBookings: calendarData?.directBookings || [],
    externalBookings: calendarData?.externalBookings || [],
    syncStatus: calendarData?.syncStatus || {},
    conflicts: calendarData?.conflicts || [],
    bookingsForSelectedDate,
  };

  const loading: CalendarLoadingState = {
    isLoadingListings,
    isLoadingBookings,
    isLoadingAny: isLoadingListings || isLoadingBookings,
    listingsError: listingsError as Error | null,
    bookingsError,
    hasError: !!(listingsError || bookingsError),
  };

  return {
    state,
    actions,
    data,
    loading,
    prefetchNextMonth: useCallback(async () => {
      const nextMonth = getNextMonth(currentMonth);
      const nextMonthStart = getMonthStart(nextMonth).toISOString().split('T')[0];
      const nextMonthEnd = getMonthEnd(nextMonth).toISOString().split('T')[0];
      await prefetchNextMonth(nextMonthStart, nextMonthEnd);
    }, [currentMonth, prefetchNextMonth]),
    prefetchPreviousMonth,
    invalidateBookings,
    optimisticUpdateBooking,
  };
};

export default useCalendarState;